import { TPatientInteropLog } from './interop-log.type'

export enum EInteropEntityType {
  VISIT = 'visit',
  CONSULTATION = 'consultation',
}

export type TInteropFieldChange = {
  interopLog: TPatientInteropLog
  value: unknown
}

export type TInteropEntity = {
  type: EInteropEntityType
  remoteId: string
  latestLog: TPatientInteropLog
  data: Record<string, unknown>
  /** Logs sharing the entity's remoteType/remoteId that are not the latest, oldest first */
  history: TPatientInteropLog[]
  /** For each field, its successive values (first value included), oldest first */
  fieldHistories: Record<string, TInteropFieldChange[]>
}

export type TInteropVisitNode = {
  visit: TInteropEntity
  consultations: TInteropEntity[]
}

export type TInteropEntityTree = {
  visits: TInteropVisitNode[]
  /** Consultations whose visit is unknown or not received */
  consultationsWithoutVisit: TInteropEntity[]
}

const getEmissionTime = (interopLog: TPatientInteropLog) =>
  new Date(interopLog.payload?.timestamp ?? interopLog.createdAt).getTime()

const getEntityData = (
  interopLog: TPatientInteropLog,
  type: EInteropEntityType,
): Record<string, unknown> | undefined =>
  type === EInteropEntityType.VISIT ? interopLog.payload?.visit : interopLog.payload?.consultation

const toTime = (date: unknown) => {
  const time = new Date(date as string).getTime()
  return Number.isNaN(time) ? null : time
}

const compareDescNullsLast = (timeA: number | null, timeB: number | null) => {
  if (timeA === null && timeB === null) return 0
  if (timeA === null) return 1
  if (timeB === null) return -1
  return timeB - timeA
}

const buildEntity = (
  type: EInteropEntityType,
  latestLog: TPatientInteropLog,
  history: TPatientInteropLog[],
): TInteropEntity => {
  const chronologicalLogs = [...history, latestLog].sort(
    (logA, logB) => getEmissionTime(logA) - getEmissionTime(logB),
  )

  const fieldHistories: Record<string, TInteropFieldChange[]> = {}
  for (const interopLog of chronologicalLogs) {
    const logData = getEntityData(interopLog, type)
    if (!logData) continue
    for (const [field, value] of Object.entries(logData)) {
      const changes = (fieldHistories[field] ??= [])
      const previous = changes[changes.length - 1]
      if (!previous || JSON.stringify(previous.value) !== JSON.stringify(value)) {
        changes.push({ interopLog, value })
      }
    }
  }

  const logWithData = [latestLog, ...chronologicalLogs.slice().reverse()].find((interopLog) =>
    getEntityData(interopLog, type),
  )

  return {
    type,
    remoteId: latestLog.remoteId as string,
    latestLog,
    data: (logWithData && getEntityData(logWithData, type)) ?? {},
    history: chronologicalLogs.filter((interopLog) => interopLog !== latestLog),
    fieldHistories,
  }
}

const getVisitNumberOfConsultation = (consultation: TInteropEntity) =>
  [consultation.latestLog, ...consultation.history.slice().reverse()].find(
    (interopLog) => interopLog.payload?.visit?.number,
  )?.payload?.visit?.number

/**
 * Builds the visit > consultations hierarchy out of the InteropLogs of a patient.
 * Entities are the logs flagged `isLatest`, field histories come from the
 * non-latest logs sharing the same remoteType and remoteId.
 */
export const buildInteropEntityTree = (interopLogs: TPatientInteropLog[]): TInteropEntityTree => {
  const historyByEntityKey = new Map<string, TPatientInteropLog[]>()
  for (const interopLog of interopLogs) {
    if (interopLog.isLatest || !interopLog.remoteType || !interopLog.remoteId) continue
    const entityKey = `${interopLog.remoteType}:${interopLog.remoteId}`
    historyByEntityKey.set(entityKey, [...(historyByEntityKey.get(entityKey) ?? []), interopLog])
  }

  const buildEntitiesOfType = (type: EInteropEntityType) =>
    interopLogs
      .filter((interopLog) => interopLog.isLatest && interopLog.remoteType === type && interopLog.remoteId)
      .map((latestLog) =>
        buildEntity(type, latestLog, historyByEntityKey.get(`${type}:${latestLog.remoteId}`) ?? []),
      )

  const visits = buildEntitiesOfType(EInteropEntityType.VISIT).sort((visitA, visitB) =>
    compareDescNullsLast(toTime(visitA.data.admissionTime), toTime(visitB.data.admissionTime)),
  )
  const consultations = buildEntitiesOfType(EInteropEntityType.CONSULTATION).sort(
    (consultationA, consultationB) =>
      compareDescNullsLast(toTime(consultationA.data.date), toTime(consultationB.data.date)),
  )

  const visitNumbers = new Set(visits.map((visit) => visit.remoteId))
  return {
    visits: visits.map((visit) => ({
      visit,
      consultations: consultations.filter(
        (consultation) => getVisitNumberOfConsultation(consultation) === visit.remoteId,
      ),
    })),
    consultationsWithoutVisit: consultations.filter((consultation) => {
      const visitNumber = getVisitNumberOfConsultation(consultation)
      return !visitNumber || !visitNumbers.has(visitNumber)
    }),
  }
}
